import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Search, Package, Tag, Receipt } from "lucide-react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

function AddDialog({ title, fields, onAdd, testid }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const submit = async () => {
    await onAdd(vals);
    setOpen(false);
    setVals({});
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={testid} size="sm" className="bg-accent hover:bg-accent/90 text-white">
          <Plus className="w-4 h-4 mr-1" /> Tambah
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input type={f.type || "text"} value={vals[f.key] ?? ""}
                onChange={(e) => setVals((v) => ({ ...v, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="dialog-add-submit" className="bg-accent hover:bg-accent/90 text-white">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Catalog({ business }) {
  const [products, setProducts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const bid = business.id;

  const load = async () => {
    const [p, pr, o] = await Promise.all([
      api.get(`/businesses/${bid}/products`),
      api.get(`/businesses/${bid}/promos`),
      api.get(`/businesses/${bid}/orders`),
    ]);
    setProducts(p.data); setPromos(pr.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, [bid]);

  const del = async (entity, id) => {
    await api.delete(`/businesses/${bid}/${entity}/${id}`);
    toast.success("Dihapus");
    load();
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Tabs defaultValue="products">
      <TabsList>
        <TabsTrigger value="products" data-testid="tab-products"><Package className="w-3.5 h-3.5 mr-1" /> Produk</TabsTrigger>
        <TabsTrigger value="promos" data-testid="tab-promos"><Tag className="w-3.5 h-3.5 mr-1" /> Promo</TabsTrigger>
        <TabsTrigger value="orders" data-testid="tab-orders"><Receipt className="w-3.5 h-3.5 mr-1" /> Pesanan</TabsTrigger>
      </TabsList>

      <TabsContent value="products" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="search-catalog-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari produk..." className="pl-9" />
          </div>
          <AddDialog title="Tambah Produk" testid="btn-add-product"
            fields={[
              { key: "name", label: "Nama Produk" }, { key: "price", label: "Harga", type: "number" },
              { key: "stock", label: "Stok", type: "number" }, { key: "category", label: "Kategori" },
              { key: "description", label: "Deskripsi" },
            ]}
            onAdd={async (v) => { await api.post(`/businesses/${bid}/products`, v); toast.success("Produk ditambah"); load(); }} />
        </div>
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table data-testid="table-products-list">
            <TableHeader><TableRow>
              <TableHead>Produk</TableHead><TableHead>Kategori</TableHead>
              <TableHead>Harga</TableHead><TableHead>Stok</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.category}</TableCell>
                  <TableCell className="font-mono">{fmt(p.price)}</TableCell>
                  <TableCell>
                    <Badge data-testid="badge-stock-status" variant="outline"
                      className={p.stock > 0 ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-destructive/10 text-destructive border-destructive/20"}>
                      {p.stock > 0 ? `${p.stock} pcs` : "Habis"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="icon" variant="ghost" data-testid="btn-delete-product" onClick={() => del("products", p.id)}>
                      <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="promos" className="space-y-3">
        <div className="flex justify-end">
          <AddDialog title="Tambah Promo" testid="btn-add-promo"
            fields={[
              { key: "code", label: "Kode Promo" }, { key: "description", label: "Deskripsi" },
              { key: "discount", label: "Diskon (mis. 20%)" },
            ]}
            onAdd={async (v) => { await api.post(`/businesses/${bid}/promos`, { ...v, active: true }); toast.success("Promo ditambah"); load(); }} />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((pr) => (
            <div key={pr.id} className="rounded-2xl border border-dashed border-accent/40 bg-card p-4 relative">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-accent">{pr.code}</span>
                <Badge variant="outline" className={pr.active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-muted"}>
                  {pr.active ? "Aktif" : "Nonaktif"}
                </Badge>
              </div>
              <p className="text-sm mt-2">{pr.description}</p>
              <p className="text-xs text-muted-foreground mt-1">Diskon: {pr.discount}</p>
              <Button size="icon" variant="ghost" className="absolute bottom-2 right-2" onClick={() => del("promos", pr.id)}>
                <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="orders" className="space-y-3">
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table data-testid="table-orders-list">
            <TableHeader><TableRow>
              <TableHead>Kode</TableHead><TableHead>Customer</TableHead>
              <TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono text-xs">{o.order_code}</TableCell>
                  <TableCell className="font-medium">{o.customer_name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{o.items}</TableCell>
                  <TableCell className="font-mono">{fmt(o.total)}</TableCell>
                  <TableCell><Badge variant="outline">{o.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}
